import { Injector, OnDestroy, TemplateRef } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap';

import { AbstractDialogService } from '@wm/core';

import { IDialog, IWidgetConfig } from '../../../framework/types';
import { BaseComponent } from '../../base/base.component';

const openedDialogs = [];

let eventsRegistered = false;

const handleDialogOpen = ref => {
    openedDialogs.push(ref);
};

const invokeOpenedCallback = (ref) => {
    handleDialogOpen(ref);
    if (ref) {
        setTimeout(() => {
            ref.invokeEventCallback('opened', {$event: {type: 'opened'}});
        });
    }
};

const invokeClosedCallback = () => {
    const ref = openedDialogs.pop();
    ref.invokeEventCallback('close');
    ref.dialogRef = undefined;
};

export abstract class BaseDialog extends BaseComponent implements IDialog, OnDestroy {

    public name: string;

    private readonly dialogService: AbstractDialogService;
    private readonly bsModal: BsModalService;

    private dialogRef: BsModalRef;

    protected constructor(
        inj: Injector,
        widgetConfig: IWidgetConfig,
        protected modalOptions: ModalOptions
    ) {
        super(inj, widgetConfig);
        this.dialogService = inj.get(AbstractDialogService);
        this.bsModal = inj.get(BsModalService);

        if (!eventsRegistered) {
            eventsRegistered = true;
            this.bsModal.onShown.subscribe(() => invokeOpenedCallback(this));

            this.bsModal.onHidden.subscribe(() => invokeClosedCallback());
        }
    }

    /**
     * Opens the dialog
     * Subscribe to the onShown event emitter of bsModal and trigger on-opened event callback
     */
    public open(initState?: any) {
        // extend the context with the initState
        Object.assign(this.context, initState);

        this.dialogRef = this.bsModal.show(this.getTemplateRef(), this.modalOptions);
    }

    /**
     * closes the dialog
     * invokes the on-close event callback
     */
    public close() {
        if (this.dialogRef) {
            this.dialogRef.hide();
        }
    }

    /**
     * Register the dialog with the dialog service for programmatic access
     */
    protected register() {
        this.dialogService.register(this.name, this);
    }

    /**
     * subclasses of BaseDialog must implement this method to return the proper template element ref
     * bsModal will use this refence to open the dialog
     * @returns {TemplateRef<any>}
     */
    protected abstract getTemplateRef(): TemplateRef<any>;

    protected onPropertyChange(key: string, nv: any, ov?: any) {
        // ignore the class attribute.
        // Prevent the framework from setting the class on the host element.
        if (key === 'class' || key === 'name' || key === 'tabindex') {
            return;
        } else if (key === 'animation') {
            this.modalOptions.class = this.modalOptions.class.replace('animated ' + ov, '');
            if (nv) {
                this.modalOptions.class = this.modalOptions.class + 'animated ' + nv;
            }
        }
        super.onPropertyChange(key, nv, ov);
    }

    public ngOnDestroy() {
        this.close();
        super.ngOnDestroy();
    }
}
